import { useLocalStorageState } from 'ahooks';
import { useMemo } from 'react';
import { isEmpty } from 'ramda';
import { LocalStorageKey } from '@/utils/storageKeys';
import { ALLOW_FEATURE_FLAGS } from '@/configs';
import { FeatureFlags } from '../types';
import { FeatureFlagNames } from '../feature-flag';

type FeatureFlagSettings = Partial<FeatureFlags>;

type UseFeatureFlagsReturns = [
  featureFlags: FeatureFlags,
  setFeatureFlag: (featureName: keyof FeatureFlags, value: boolean) => void,
];

export const useFeatureFlags = (): UseFeatureFlagsReturns => {
  const [featureFlagSettings, setFeatureFlagSettings] = useLocalStorageState<FeatureFlagSettings | undefined>(
    LocalStorageKey.FeatureFlagSettings,
    {
      defaultValue: undefined,
      listenStorageChange: true,
    }
  );

  const featureFlags: FeatureFlags = useMemo(() => {
    return FeatureFlagNames.reduce<FeatureFlags>((acc, featureName) => {
      acc[featureName] = ALLOW_FEATURE_FLAGS && !!featureFlagSettings?.[featureName];
      return acc;
    }, {} as FeatureFlags);
  }, [featureFlagSettings]);

  const setFeatureFlag = (featureName: keyof FeatureFlags, value: boolean): void => {
    if (!ALLOW_FEATURE_FLAGS) return;

    const newFeatureFlagSettings: FeatureFlagSettings = { ...featureFlagSettings };

    // use this chance to remove feature flags that are not defined in FeatureFlagNames
    Object.keys(newFeatureFlagSettings).forEach((key) => {
      if (!FeatureFlagNames.includes(key as keyof FeatureFlags)) {
        delete newFeatureFlagSettings[key as keyof FeatureFlags];
      }
    });

    // to keep only enabled feature flags in the storage
    if (value) {
      newFeatureFlagSettings[featureName] = value;
    } else {
      delete newFeatureFlagSettings[featureName];
    }

    setFeatureFlagSettings(isEmpty(newFeatureFlagSettings) ? undefined : newFeatureFlagSettings);
  };

  return [featureFlags, setFeatureFlag];
};
